import { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import {
  listTripExpenses,
  createTripExpense,
  updateTripExpense,
  deleteTripExpense,
  exportTripExpenses,
  type TripExpensePayload,
} from '../api/tripExpenseApi';
import { getTrips } from '../api/tripApi';

interface FormData {
  tripId: string;
  type: 'fuel' | 'toll' | 'other';
  amount: number | '';
  note: string;
  date: string;
}

const initialFormState: FormData = {
  tripId: '',
  type: 'fuel',
  amount: '',
  note: '',
  date: new Date().toISOString().slice(0, 10),
};

const typeLabels = {
  fuel: 'Carburant',
  toll: 'Péage',
  other: 'Divers',
};

export default function TripExpenses() {
  const [rows, setRows] = useState<any[]>([]);
  const [trips, setTrips] = useState<any[]>([]);
  const [filterTripId, setFilterTripId] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form, setForm] = useState<FormData>(initialFormState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const load = async () => {
    try {
      setLoading(true);
      setError('');

      const [exp, t] = await Promise.all([
        listTripExpenses(filterTripId || undefined),
        getTrips({ limit: 200 }),
      ]);

      const tripList = (t as any).rows || (t as any).trips || [];
      setTrips(tripList);
      setRows(exp || []);
    } catch (error: any) {
      console.error('Error loading data:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filterTripId]);

  const resetForm = () => {
    setForm({ ...initialFormState });
  };

  const openAddDialog = () => {
    setEditing(null);
    resetForm();
    setOpen(true);
    setError('');
  };

  const openEditDialog = (expense: any) => {
    setEditing(expense);
    setForm({
      tripId: expense.tripId || '',
      type: expense.type || 'fuel',
      amount: expense.amount || '',
      note: expense.note || '',
      date: expense.date
        ? new Date(expense.date).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10),
    });
    setOpen(true);
    setError('');
  };

  const closeDialog = () => {
    setOpen(false);
    setEditing(null);
    resetForm();
    setError('');
  };

  const validateForm = (): string | null => {
    if (!form.tripId.trim()) return 'Veuillez sélectionner une course';
    if (!form.type) return 'Veuillez sélectionner un type de dépense';
    if (!form.amount || Number(form.amount) <= 0)
      return 'Le montant doit être supérieur à 0';
    if (!form.date) return 'Veuillez sélectionner une date';

    // Vérifier que la date est valide
    const selectedDate = new Date(form.date);
    if (isNaN(selectedDate.getTime())) return 'Date invalide';

    return null;
  };

  const onSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const payload: TripExpensePayload = {
        tripId: form.tripId.trim(),
        type: form.type,
        amount: Number(form.amount),
        note: form.note.trim() || null,
        date: new Date(form.date).toISOString(),
      };

      console.log('Submitting expense payload:', payload);

      if (editing) {
        const updated = await updateTripExpense(editing.id, payload);
        setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      } else {
        const created = await createTripExpense(payload);
        setRows((prev) => [created, ...prev]);
      }

      closeDialog();
    } catch (error: any) {
      console.error('Error saving trip expense:', error);
      const errorMessage =
        error.response?.data?.error ||
        "Erreur lors de l'enregistrement de la dépense";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (expenseId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette dépense ?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteTripExpense(expenseId);
      setRows((prev) => prev.filter((r) => r.id !== expenseId));
    } catch (error: any) {
      console.error('Error deleting trip expense:', error);
      alert('Erreur lors de la suppression de la dépense');
    } finally {
      setLoading(false);
    }
  };

  const formatTripDisplay = (trip: any) => {
    if (!trip) return 'N/A';
    const date = new Date(trip.date).toLocaleDateString();
    const driver = trip.driver?.nom || trip.driverId || 'N/A';
    const company = trip.company?.name || trip.companyId || 'N/A';
    return `${date} — ${driver} — ${company}`;
  };

  const getTripById = (tripId: string) => {
    return trips.find((t) => t.id === tripId);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Box
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          <Typography variant="h4">Dépenses de courses</Typography>
          <Typography color="text.secondary">
            Associez des dépenses (carburant, péage, autres) à une course.
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" onClick={() => exportTripExpenses({ tripId: filterTripId || undefined }, 'csv')} disabled={loading}>Export CSV</Button>
          <Button variant="outlined" onClick={() => exportTripExpenses({ tripId: filterTripId || undefined }, 'xlsx')} disabled={loading}>Export Excel</Button>
          <Button variant="outlined" onClick={() => exportTripExpenses({ tripId: filterTripId || undefined }, 'pdf')} disabled={loading}>Export PDF</Button>
          <Button variant="contained" onClick={openAddDialog} disabled={loading}>
            + Ajouter
          </Button>
        </Box>
      </Box>

      {error && !open && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box mb={2} display="flex" gap={2}>
        <TextField
          select
          label="Course"
          value={filterTripId}
          onChange={(e) => setFilterTripId(e.target.value)}
          sx={{ minWidth: 350 }}
          disabled={loading}
        >
          <MenuItem value="">Toutes</MenuItem>
          {trips.map((trip: any) => (
            <MenuItem key={trip.id} value={trip.id}>
              {formatTripDisplay(trip)}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Type</TableCell>
            <TableCell align="right">Montant (F CFA)</TableCell>
            <TableCell>Note</TableCell>
            <TableCell>Course</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                Chargement...
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                Aucune dépense trouvée
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const trip = getTripById(row.tripId);
              return (
                <TableRow key={row.id}>
                  <TableCell>
                    {new Date(row.date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {typeLabels[row.type as keyof typeof typeLabels] ||
                      row.type}
                  </TableCell>
                  <TableCell align="right">
                    {Number(row.amount).toFixed(2)}
                  </TableCell>
                  <TableCell>{row.note || '-'}</TableCell>
                  <TableCell>
                    {trip ? formatTripDisplay(trip) : row.tripId}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => openEditDialog(row)}
                      disabled={loading}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(row.id)}
                      disabled={loading}
                      color="error"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing ? 'Modifier dépense' : 'Nouvelle dépense'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box mt={1} display="grid" gap={2}>
            <TextField
              select
              label="Course *"
              value={form.tripId}
              onChange={(e) =>
                setForm((f) => ({ ...f, tripId: e.target.value }))
              }
              disabled={loading}
              fullWidth
              required
            >
              <MenuItem value="">-- Sélectionnez une course --</MenuItem>
              {trips.map((trip: any) => (
                <MenuItem key={trip.id} value={trip.id}>
                  {formatTripDisplay(trip)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Type *"
              value={form.type}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  type: e.target.value as FormData['type'],
                }))
              }
              disabled={loading}
              fullWidth
              required
            >
              <MenuItem value="fuel">Carburant</MenuItem>
              <MenuItem value="toll">Péage</MenuItem>
              <MenuItem value="other">Divers</MenuItem>
            </TextField>

            <TextField
              type="number"
              label="Montant *"
              value={form.amount}
              onChange={(e) => {
                const value = e.target.value;
                setForm((f) => ({
                  ...f,
                  amount: value === '' ? '' : Number(value),
                }));
              }}
              disabled={loading}
              fullWidth
              required
              inputProps={{
                min: 0,
                step: 0.01,
              }}
              helperText="Montant en F CFA"
            />

            <TextField
              label="Note"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              disabled={loading}
              fullWidth
              multiline
              rows={2}
              placeholder="Description optionnelle de la dépense..."
            />

            <TextField
              type="date"
              label="Date *"
              InputLabelProps={{ shrink: true }}
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              disabled={loading}
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={loading}>
            Annuler
          </Button>
          <Button variant="contained" onClick={onSubmit} disabled={loading}>
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
